import { Users } from 'lucide-react';
import { SectionIcon } from '../ui/SectionIcon.js';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ContactReportSummary, InvestorSummary } from '@nemo/api';
import { WARMTHS, compareWarmth, throughWords, type Warmth } from '@nemo/core/model';
import { TONE_VARS, type PicklistOption } from '../grid/cells/index.js';
import { useTRPC } from '../trpc.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { formatDay } from '../ui/format.js';
import { LogContact } from './LogContact.js';
import { panelPlace } from '../people/roleClass.js';
import { Menu, MenuSurface, type MenuItem } from '../ui/Menu.js';
import { useToast } from '../ui/Toast.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

export interface PanePeopleProps {
  fundId: string;
  fundName: string;
  members: readonly PicklistOption[];
  /** Whether the pane is the phone sheet, which has no room for a heading row. */
  sheet: boolean;
  /** Who to scroll to and mark, when the pane was opened from a link to them. */
  focusPerson: string | null;
}

/** What a warmth menu is open against: the pair, and the pill it hangs from. */
interface WarmthTarget {
  investor: InvestorSummary;
  edge: InvestorSummary['knownBy'][number];
  pill: HTMLElement;
}

/**
 * Everything irreversible asks first, and the question names what it is about
 * to remove rather than what kind of thing it is.
 */
type Confirmation =
  | { kind: 'removePerson'; investorId: string; personName: string }
  | {
      kind: 'deleteRelationship';
      investorId: string;
      memberId: string;
      memberName: string;
      personName: string;
    };

/**
 * Why the pane is opened at all: who do we know there. A person is a line —
 * their name, what they do, and one pill per colleague who knows them, tinted
 * by how well and dated by when Affinity last saw them speak. Somebody who has
 * moved on is still on file, folded away rather than deleted, with where the
 * list has them now.
 */
export function PanePeople(props: PanePeopleProps): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const investors = useQuery(trpc.investors.list.queryOptions({ fundId: props.fundId }));
  // Everything filed about this fund's people, in one ask. Reports about a
  // name nobody could place are not here: they are in the review queue, and
  // showing them under a person would be claiming the match nobody made.
  const reports = useQuery(
    trpc.contacts.list.queryOptions({ fundId: props.fundId, status: 'matched' }),
  );

  const [adding, setAdding] = useState(false);
  const [logging, setLogging] = useState(false);
  const [newName, setNewName] = useState('');
  const [warmthTarget, setWarmthTarget] = useState<WarmthTarget | null>(null);
  const [openNotes, setOpenNotes] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  function refresh(): void {
    void queryClient.invalidateQueries(trpc.investors.list.pathFilter());
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.contacts.list.queryKey() });
  }

  const onError = (error: { message: string }): void => toast(error.message);
  const createInvestor = useMutation(
    trpc.investors.create.mutationOptions({ onSuccess: refresh, onError }),
  );
  const deleteInvestor = useMutation(
    trpc.investors.delete.mutationOptions({ onSuccess: refresh, onError }),
  );
  const upsert = useMutation(
    trpc.relationships.upsert.mutationOptions({ onSuccess: refresh, onError }),
  );
  const deleteRelationship = useMutation(
    trpc.relationships.delete.mutationOptions({ onSuccess: refresh, onError }),
  );

  function confirmed(pending: Confirmation): void {
    setConfirmation(null);
    if (pending.kind === 'removePerson') {
      deleteInvestor.mutate({ id: pending.investorId });
      return;
    }
    deleteRelationship.mutate({ memberId: pending.memberId, investorId: pending.investorId });
  }

  const people = investors.data?.rows ?? [];
  const here = people.filter((person) => !person.hasLeft).sort(comparePeople);
  // A link to somebody lands on them wherever their role would put them.
  const place = (investor: InvestorSummary): ReturnType<typeof panelPlace> =>
    investor.id === props.focusPerson ? 'shown' : panelPlace(investor.roleClass);
  const shown = here.filter((investor) => place(investor) === 'shown');
  const adjacent = here.filter((investor) => place(investor) === 'adjacent');
  const hiddenSupport = here.filter((investor) => place(investor) === 'hidden').length;
  const left = people.filter((person) => person.hasLeft).sort(comparePeople);
  const reportsByPerson = byPerson(reports.data ?? []);

  const person = (investor: InvestorSummary): React.JSX.Element => (
    <Person
      key={investor.id}
      investor={investor}
      fundName={props.fundName}
      members={props.members}
      reports={reportsByPerson.get(investor.id) ?? []}
      focused={investor.id === props.focusPerson}
      openNotes={openNotes}
      onWarmth={(edge, pill) => setWarmthTarget({ investor, edge, pill })}
      onAddMember={(memberId) =>
        upsert.mutate({ memberId, investorId: investor.id, warmth: 'unknown', notes: null })
      }
      onSaveNotes={(edge, notes) => {
        setOpenNotes(null);
        upsert.mutate({
          memberId: edge.memberId,
          investorId: investor.id,
          warmth: edge.warmth,
          notes,
        });
      }}
      onCancelNotes={() => setOpenNotes(null)}
      onRemove={() =>
        setConfirmation({
          kind: 'removePerson',
          investorId: investor.id,
          personName: investor.name,
        })
      }
    />
  );

  return (
    <section className="pane-section">
      <h3 className="pane-section-title">
        <SectionIcon icon={Users} />
        {props.sheet ? 'People · known by' : 'People'}
      </h3>

      {investors.isPending && <p className="subtle">Loading…</p>}
      {!investors.isPending && people.length === 0 && (
        <p className="subtle">Nobody is on file at this fund.</p>
      )}

      {!props.sheet && people.length > 0 && (
        <div className="pane-people-heads">
          <span className="pane-people-head">Person</span>
          <span className="pane-people-head">Known by</span>
          <span className="pane-people-head pane-people-head-seen">Last seen</span>
          <span />
        </div>
      )}

      <div className="pane-list">{shown.map(person)}</div>

      {adjacent.length > 0 && (
        <details className="pane-disclosure">
          <summary>Advisers, venture partners and scouts ({adjacent.length})</summary>
          <div className="pane-list">{adjacent.map(person)}</div>
        </details>
      )}

      {hiddenSupport > 0 && (
        <p className="subtle">
          {hiddenSupport === 1
            ? '1 support role (assistant, finance, talent, platform) not shown.'
            : `${hiddenSupport} support roles (assistants, finance, talent, platform) not shown.`}
        </p>
      )}

      {left.length > 0 && (
        <details
          className="pane-disclosure"
          // A link to somebody who has moved on still has to land on them.
          open={left.some((investor) => investor.id === props.focusPerson) || undefined}
        >
          <summary>Former · left the fund ({left.length})</summary>
          <div className="pane-list">{left.map(person)}</div>
        </details>
      )}

      {adding ? (
        <form
          className="pane-add"
          onSubmit={(event) => {
            event.preventDefault();
            if (newName.trim() === '') return;
            createInvestor.mutate({ fundId: props.fundId, name: newName.trim() });
            setNewName('');
            setAdding(false);
          }}
        >
          <input
            autoFocus
            placeholder="Their name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return;
              event.stopPropagation();
              setAdding(false);
            }}
          />
          <button className="button small" type="submit">
            Add
          </button>
        </form>
      ) : (
        <div className="pane-actions">
          <button className="button small" type="button" onClick={() => setAdding(true)}>
            Add person
          </button>
          <button className="button small" type="button" onClick={() => setLogging(true)}>
            Log a contact
          </button>
        </div>
      )}

      {logging && (
        <LogContact
          fundId={props.fundId}
          fundName={props.fundName}
          people={here.map((investor) => ({ id: investor.id, name: investor.name }))}
          onClose={() => setLogging(false)}
        />
      )}

      {warmthTarget !== null && (
        <MenuSurface
          anchor={{ kind: 'element', element: warmthTarget.pill }}
          align="end"
          label={`How well ${warmthTarget.edge.memberName} knows ${warmthTarget.investor.name}`}
          onClose={() => setWarmthTarget(null)}
          items={warmthItems(warmthTarget, {
            onWarmth: (warmth) =>
              upsert.mutate({
                memberId: warmthTarget.edge.memberId,
                investorId: warmthTarget.investor.id,
                warmth,
                notes: warmthTarget.edge.notes,
              }),
            onNotes: () =>
              setOpenNotes(noteKey(warmthTarget.investor.id, warmthTarget.edge.memberId)),
            onDelete: () =>
              setConfirmation({
                kind: 'deleteRelationship',
                investorId: warmthTarget.investor.id,
                memberId: warmthTarget.edge.memberId,
                memberName: warmthTarget.edge.memberName,
                personName: warmthTarget.investor.name,
              }),
          })}
        />
      )}

      {confirmation !== null && (
        <ConfirmModal
          {...copyFor(confirmation, props.fundName)}
          danger
          onConfirm={() => confirmed(confirmation)}
          onCancel={() => setConfirmation(null)}
        />
      )}
    </section>
  );
}

/**
 * How a warmth reads out loud. "unknown" is not a temperature somebody chose,
 * it is the absence of a judgement, so it says so rather than leaving a reader
 * to work out whether the relationship or the record is the unknown one.
 */
function warmthLabel(warmth: Warmth): string {
  return warmth === 'unknown' ? 'not rated' : warmth;
}

/** Which relationship has its notes open, keyed by the pair that owns them. */
function noteKey(investorId: string, memberId: string): string {
  return `${investorId}:${memberId}`;
}

type KnownByEdge = InvestorSummary['knownBy'][number];

/**
 * How one relationship compares to another for "best pill" purposes: warmth
 * rank first (a person's judgement outranks any measurement), then Affinity's
 * known here before known through another fund, then measured strength, then the
 * most recent contact. Same chain `compareWhoKnows`
 * (`@nemo/core/model`) uses, but that comparator is shaped for ordering the
 * pills *within* one person's list — it ties on `memberName` before the person
 * it is about, which is wrong once the comparison is between two different
 * people. Written locally rather than reused for that reason.
 */
function compareEdgeRank(left: KnownByEdge, right: KnownByEdge): number {
  return (
    compareWarmth(left.warmth, right.warmth) ||
    Number(left.through !== null) - Number(right.through !== null) ||
    (right.affinityStrength ?? -1) - (left.affinityStrength ?? -1) ||
    (right.lastContactAt ?? '').localeCompare(left.lastContactAt ?? '')
  );
}

function bestEdge(edges: readonly KnownByEdge[]): KnownByEdge {
  return edges.reduce((best, edge) => (compareEdgeRank(edge, best) < 0 ? edge : best));
}

/**
 * The best way in first. Somebody nobody knows at all is not a weak
 * relationship, it is the absence of one, so they drop below everybody who
 * has at least one pill, however faint.
 */
function comparePeople(left: InvestorSummary, right: InvestorSummary): number {
  if (left.knownBy.length === 0 || right.knownBy.length === 0) {
    if (left.knownBy.length === right.knownBy.length) return left.name.localeCompare(right.name);
    return left.knownBy.length === 0 ? 1 : -1;
  }
  return (
    compareEdgeRank(bestEdge(left.knownBy), bestEdge(right.knownBy)) ||
    left.name.localeCompare(right.name)
  );
}

interface PersonProps {
  investor: InvestorSummary;
  fundName: string;
  members: readonly PicklistOption[];
  /** What has been reported about them, newest conversation first. */
  reports: readonly ContactReportSummary[];
  focused: boolean;
  openNotes: string | null;
  onWarmth: (edge: InvestorSummary['knownBy'][number], pill: HTMLElement) => void;
  onAddMember: (memberId: string) => void;
  onSaveNotes: (edge: InvestorSummary['knownBy'][number], notes: string) => void;
  onCancelNotes: () => void;
  onRemove: () => void;
}

function Person(props: PersonProps): React.JSX.Element {
  const { investor, focused } = props;
  const unlisted = props.members.filter(
    // Not a colleague shown through another fund: they may hold a colder row on
    // this role, and adding would overwrite its warmth and notes.
    (member) => !investor.knownBy.some((edge) => edge.memberId === member.id),
  );
  const row = useRef<HTMLDivElement>(null);
  const [showFormer, setShowFormer] = useState(false);

  useEffect(() => {
    if (focused) row.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);

  return (
    <div className={focused ? 'person focused' : 'person'} ref={row}>
      <div className="person-line">
        <span className="person-who">
          <span className="person-name-tip">
            <span className="person-name" tabIndex={0} aria-describedby={`checked-${investor.id}`}>
              {investor.name}
            </span>
            <span className="person-tip" role="tooltip" id={`checked-${investor.id}`}>
              {checkedPhrase(investor, props.fundName)}
            </span>
          </span>
          {investor.title !== null && <span className="person-role">{investor.title}</span>}
          {investor.nowAt.length > 0 && (
            <span className="person-role">
              now at {investor.nowAt.map((place) => place.fundName).join(', ')}
            </span>
          )}
        </span>

        <span className="person-pills">
          {investor.knownBy.map((edge) => (
            <span className="pill-pair" key={edge.memberId}>
              {edge.through === null ? (
                <button
                  type="button"
                  className="pill"
                  style={{
                    background: `var(${TONE_VARS[edge.warmth].background})`,
                    color: `var(${TONE_VARS[edge.warmth].text})`,
                  }}
                  title={
                    edge.warmthDerived
                      ? 'Set from Affinity, not by a person'
                      : `How well ${edge.memberName} knows ${investor.name}`
                  }
                  onClick={(event) => props.onWarmth(edge, event.currentTarget)}
                >
                  {edge.memberName} · {warmthLabel(edge.warmth)}
                  <span className="pill-chevron" aria-hidden="true">
                    ▾
                  </span>
                </button>
              ) : (
                // Not a button: the warmth is a judgement about the other role,
                // and setting it here would file a new relationship on this one.
                <span
                  className="pill carried"
                  style={{
                    background: `var(${TONE_VARS[edge.warmth].background})`,
                    color: `var(${TONE_VARS[edge.warmth].text})`,
                  }}
                  title={`${edge.memberName} knows ${investor.name}, ${throughWords(edge.through)}. Change it on ${edge.through.fundName}.`}
                >
                  {edge.memberName} · {warmthLabel(edge.warmth)}
                </span>
              )}
              <LastSeen edge={edge} />
              {edge.through !== null && (
                <span className="pill-through">{throughWords(edge.through)}</span>
              )}
            </span>
          ))}

          {investor.knownByDeparted.length > 0 && (
            <button
              type="button"
              className="former-toggle"
              aria-expanded={showFormer}
              onClick={() => setShowFormer((open) => !open)}
            >
              {showFormer ? '−' : '+'}
              {investor.knownByDeparted.length} former or support
            </button>
          )}

          {showFormer &&
            investor.knownByDeparted.map((edge) => (
              <span className="pill-pair" key={edge.memberId}>
                {/* Not a button: there is no warmth to set on a relationship
                    nobody can act on, and a chevron would offer one. */}
                <span
                  className="pill departed"
                  title={`${edge.memberName} knew ${investor.name}, but is not a way in: ${edge.memberLabel}`}
                >
                  {edge.memberLabel} · {warmthLabel(edge.warmth)}
                  {edge.through === null ? '' : ` · ${throughWords(edge.through)}`}
                </span>
                <LastSeen edge={edge} />
              </span>
            ))}
        </span>

        <span className="person-commands">
          <Menu
            className="pill-add"
            align="end"
            label={`Add somebody who knows ${investor.name}`}
            items={
              unlisted.length === 0
                ? [
                    {
                      label: 'Everyone is already listed',
                      disabled: true,
                      onSelect: () => undefined,
                    },
                  ]
                : unlisted.map((member) => ({
                    label: member.label,
                    onSelect: () => props.onAddMember(member.id),
                  }))
            }
          >
            +
          </Menu>

          <button
            type="button"
            className="icon-button danger person-remove"
            aria-label={`Remove ${investor.name} from ${props.fundName}`}
            title={`Remove ${investor.name} from ${props.fundName}`}
            onClick={props.onRemove}
          >
            ✕
          </button>
        </span>
      </div>

      {showFormer &&
        investor.knownByDeparted.map((edge) =>
          edge.notes === null ? null : (
            <p className="person-note" key={edge.memberId}>
              {edge.memberLabel}: {edge.notes}
            </p>
          ),
        )}

      {props.reports.length > 0 && <Reports reports={props.reports} />}

      {investor.knownBy.map((edge) =>
        props.openNotes === noteKey(investor.id, edge.memberId) ? (
          <NotesEditor
            key={edge.memberId}
            notes={edge.notes ?? ''}
            label={`What ${edge.memberName} notes about ${investor.name}`}
            onSave={(notes) => props.onSaveNotes(edge, notes)}
            onCancel={props.onCancelNotes}
          />
        ) : edge.notes === null ? null : (
          <p className="person-note" key={edge.memberId}>
            {edge.memberName}: {edge.notes}
          </p>
        ),
      )}
    </div>
  );
}

/**
 * What a warmth pill's date is a record of. Affinity read it out of the email
 * and calendar history; a reported one is a colleague saying it happened, and
 * a reader who cannot tell the two apart cannot judge either.
 */
function lastContactTitle(edge: KnownByEdge): string {
  switch (edge.lastContactSource) {
    case 'reported':
      return 'Last contact, from a contact somebody reported';
    case 'affinity':
      return 'Last contact, from Affinity';
    case null:
      return 'Last contact';
  }
}

/** The date column: empty when nothing is dated, so the grid row still holds. */
function LastSeen(props: { edge: KnownByEdge }): React.JSX.Element {
  const day = formatDay(props.edge.lastContactAt);
  return (
    <span className="pill-when" title={day === null ? undefined : lastContactTitle(props.edge)}>
      {day}
    </span>
  );
}

/** The reports about each person, newest conversation first. */
function byPerson(reports: readonly ContactReportSummary[]): Map<string, ContactReportSummary[]> {
  const grouped = new Map<string, ContactReportSummary[]>();
  for (const report of reports) {
    if (report.investorId === null) continue;
    const held = grouped.get(report.investorId);
    if (held === undefined) grouped.set(report.investorId, [report]);
    else held.push(report);
  }
  return grouped;
}

/** How many reports a person shows before the rest are folded away. */
const REPORTS_SHOWN = 2;

/**
 * What people have said about this person. The two most recent are the ones
 * worth the space — "what have they been doing" is a question about lately —
 * and the rest open in place rather than somewhere else.
 */
function Reports(props: { reports: readonly ContactReportSummary[] }): React.JSX.Element {
  const [all, setAll] = useState(false);
  const shown = all ? props.reports : props.reports.slice(0, REPORTS_SHOWN);
  const hidden = props.reports.length - shown.length;

  return (
    <div className="person-reports">
      {shown.map((report) => (
        <p className="person-report" key={report.id}>
          <span className="person-report-when">{formatDay(report.happenedAt)}</span>
          {' · '}
          {report.channel}
          {' · '}
          <span className="person-report-who">{report.memberName}</span>
          {report.intel === null ? '' : `: ${report.intel}`}
        </p>
      ))}
      {hidden > 0 && (
        <button className="pane-add-link" type="button" onClick={() => setAll(true)}>
          +{hidden} more
        </button>
      )}
    </div>
  );
}

/** The warmth choice, and the two other things a pair can have done to it. */
function warmthItems(
  target: WarmthTarget,
  actions: { onWarmth: (warmth: Warmth) => void; onNotes: () => void; onDelete: () => void },
): MenuItem[] {
  return [
    ...WARMTHS.map((warmth) => ({
      label: warmth === target.edge.warmth ? `${warmthLabel(warmth)} ✓` : warmthLabel(warmth),
      onSelect: () => actions.onWarmth(warmth),
    })),
    { label: 'Notes…', onSelect: actions.onNotes },
    { label: 'They do not know them', danger: true, onSelect: actions.onDelete },
  ];
}

function copyFor(
  pending: Confirmation,
  fundName: string,
): { title: string; body: string; confirmLabel: string } {
  if (pending.kind === 'removePerson') {
    return {
      title: `Remove ${pending.personName} from ${fundName}?`,
      body: 'Their relationships are removed too.',
      confirmLabel: 'Remove',
    };
  }
  return {
    title: `${pending.memberName} no longer knows ${pending.personName}?`,
    body: 'The warmth and any notes on the pair go with it.',
    confirmLabel: 'Delete',
  };
}

interface NotesEditorProps {
  notes: string;
  label: string;
  onSave: (notes: string) => void;
  onCancel: () => void;
}

/**
 * Notes save on the way out, which is what a blur is; Escape has to say so
 * before the blur it causes, or leaving by cancelling would still write.
 */
function NotesEditor(props: NotesEditorProps): React.JSX.Element {
  const settled = useRef(false);

  return (
    <input
      className="pane-editor"
      autoFocus
      defaultValue={props.notes}
      aria-label={props.label}
      placeholder="Notes"
      onBlur={(event) => {
        if (settled.current) return;
        settled.current = true;
        props.onSave(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          settled.current = true;
          props.onSave(event.currentTarget.value);
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          settled.current = true;
          props.onCancel();
        }
      }}
    />
  );
}

/** Where the source names are spelled the way a person reads them. */
const VERIFIED_VIA: Record<string, string> = {
  team_page: 'the team page',
  linkedin_search: 'LinkedIn',
  reported: 'a reported move',
};

/** The tooltip on a name: whether they are still at the fund, and how we know. */
function checkedPhrase(investor: InvestorSummary, fundName: string): string {
  const day = formatDay(investor.lastVerifiedAt);
  if (day === null) return `Still at ${fundName}? Not checked yet`;
  const via =
    investor.verifiedVia === null
      ? null
      : (VERIFIED_VIA[investor.verifiedVia] ?? investor.verifiedVia);
  return via === null
    ? `Confirmed at ${fundName} on ${day}`
    : `Confirmed at ${fundName} on ${day} via ${via}`;
}
